import { useMemo } from 'react';
import {
  Area,
  CartesianGrid,
  ComposedChart,
  Line,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import type { ScenarioCarbonBlock } from '@siliconx/shared';
import { SX_PALETTE } from '@siliconx/shared/ui/charts';
import {
  CARBON_CATEGORY_META,
  CARBON_CATEGORY_ORDER,
  categoryPointTotal,
  formatTonnes,
  kgToTonnes,
  type CarbonCategoryKey,
} from './carbonCategories';

const BORDER = 'hsl(var(--border, 240 5% 91%))';
const MUTED = 'hsl(var(--muted-foreground, 215 20% 65%))';
const BASELINE = SX_PALETTE[4]; // amber — "as-is", same meaning as ScenarioCompareChart's baseline
const TARGET = '#0f172a'; // slate-900 — black-on-paper, survives greyscale printing

const BASELINE_SERIES_NAME = 'Baseline (hold everything)';

export interface CarbonChartRow extends Record<CarbonCategoryKey, number> {
  month: number;
  baselineTotal: number;
}

/**
 * Differences the kernel's CUMULATIVE series into per-month flow, in tonnes.
 * See `CarbonForecastChart`'s docstring for why. Exported for unit test: the
 * invariant that matters is that the differences telescope back to the final
 * cumulative point (no emissions invented, none lost).
 */
export function toMonthlyChartRows(series: ScenarioCarbonBlock['series']): CarbonChartRow[] {
  return series.map((point, i) => {
    const prev = i === 0 ? null : series[i - 1]!;
    const row = {
      month: point.month,
      baselineTotal: kgToTonnes(
        categoryPointTotal(point.baseline) - (prev ? categoryPointTotal(prev.baseline) : 0),
      ),
    } as CarbonChartRow;
    for (const key of CARBON_CATEGORY_ORDER) {
      row[key] = kgToTonnes(point.scenario[key] - (prev ? prev.scenario[key] : 0));
    }
    return row;
  });
}

/**
 * SIL-440 (spec §6, Variant A row 1) — the category-stacked MONTHLY area
 * chart, with the amber dashed baseline overlay and the black dashed target
 * line.
 *
 * ── Why this differentiates the kernel's series ──────────────────────────
 * `ScenarioCarbonBlock.series` is CUMULATIVE-to-date per point (the SIL-435
 * kernel mirrors `scenarioEngine`'s own convention: a lump lands once at its
 * action month and then persists in every later point's running total). The
 * spec and the Variant-A prototype both call for a MONTHLY chart, and the
 * target is a flat monthly budget — a flat line drawn across a cumulative
 * stack would be meaningless (the cumulative curve crosses any flat line
 * exactly once, regardless of performance). So this component differences the
 * cumulative series into per-month flow before charting:
 *
 *   row[0]      = series[0]            (whatever lands at/by month 0)
 *   row[i], i>0 = series[i] - series[i-1]
 *
 * Total mass is preserved exactly (the differences telescope back to the
 * final cumulative point), and a one-time lump renders as a single-month
 * spike at its action month — which is what "Expected future emissions from
 * disposal actions taken in the month shown" (§4's Cat-5 timing helper)
 * promises the reader.
 */
export function CarbonForecastChart({
  series,
  targetTco2ePerYr,
  targetLabel,
  height = 280,
}: {
  series: ScenarioCarbonBlock['series'];
  /** The `carbon_target` row matching the ACTIVE FILTER SCOPE (spec §4), or
   * null when no target is set for that scope — no line is drawn then, and
   * no placeholder zero is invented. */
  targetTco2ePerYr: number | null;
  targetLabel: string | null;
  height?: number;
}) {
  const rows = useMemo(() => toMonthlyChartRows(series), [series]);

  if (rows.length === 0) {
    return (
      <div className="flex items-center justify-center text-xs" style={{ height, color: MUTED }}>
        No months to project — check the scenario&apos;s cohorts.
      </div>
    );
  }

  const monthlyTarget = targetTco2ePerYr === null ? null : targetTco2ePerYr / 12;

  return (
    <ResponsiveContainer width="100%" height={height}>
      <ComposedChart data={rows} margin={{ top: 8, right: 12, bottom: 0, left: 4 }}>
        <CartesianGrid stroke={BORDER} strokeDasharray="3 3" vertical={false} />
        <XAxis
          dataKey="month"
          tick={{ fontSize: 12, fill: MUTED }}
          tickLine={false}
          axisLine={{ stroke: BORDER }}
          tickFormatter={(m: number) => `M${m}`}
        />
        <YAxis
          tick={{ fontSize: 12, fill: MUTED }}
          tickLine={false}
          axisLine={false}
          width={52}
          tickFormatter={(v: number) => `${v.toLocaleString('en-US', { maximumFractionDigits: 1 })} t`}
        />
        <Tooltip
          contentStyle={{ fontSize: 12, borderRadius: 6, borderColor: BORDER }}
          labelFormatter={(m: number) => `Month ${m} — emitted this month`}
          formatter={(value: number, name: string) => [formatTonnes(value), name]}
        />
        {CARBON_CATEGORY_ORDER.map((key) => (
          <Area
            key={key}
            dataKey={key}
            stackId="ghg-category"
            name={CARBON_CATEGORY_META[key].label}
            fill={CARBON_CATEGORY_META[key].color}
            fillOpacity={0.85}
            stroke="none"
            isAnimationActive={false}
          />
        ))}
        <Line
          dataKey="baselineTotal"
          name={BASELINE_SERIES_NAME}
          stroke={BASELINE}
          strokeWidth={2}
          strokeDasharray="4 3"
          dot={false}
          isAnimationActive={false}
        />
        {monthlyTarget !== null && targetLabel !== null && (
          <ReferenceLine
            y={monthlyTarget}
            stroke={TARGET}
            strokeWidth={1.5}
            strokeDasharray="8 4"
            ifOverflow="extendDomain"
            label={{
              value: targetLabel,
              position: 'insideTopRight',
              fontSize: 12,
              fill: TARGET,
            }}
          />
        )}
      </ComposedChart>
    </ResponsiveContainer>
  );
}
